import React, { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../context/TranslationContext";
import { useDialogFocusTrap, useDialogScrollLock } from "../hooks/useDialogFocusTrap";
import "../styles/AnnouncementModal.css";

export const renderBodyItem = (item, index) => {
  if (typeof item === "string") return <p key={index}>{item}</p>;
  if (!item || typeof item !== "object") return null;

  if (Array.isArray(item.list)) {
    const toned = item.list.some((entry) => entry && typeof entry === "object" && entry.tone);
    return (
      <ul
        key={index}
        className={`announcement-modal__list${toned ? " announcement-modal__list--toned" : ""}`}
      >
        {item.list.map((entry, entryIndex) => {
          if (entry && typeof entry === "object") {
            return (
              <li
                key={entryIndex}
                className={`announcement-modal__tone announcement-modal__tone--${entry.tone}`}
              >
                <span className="announcement-modal__tone-dot" aria-hidden="true" />
                <span>{entry.text}</span>
              </li>
            );
          }
          return <li key={entryIndex}>{entry}</li>;
        })}
      </ul>
    );
  }

  if (item.href) {
    const externalProps = item.external
      ? { target: "_blank", rel: "noopener noreferrer" }
      : {};
    return (
      <p key={index}>
        {item.label ? `${item.label} ` : null}
        <a className="announcement-modal__link" href={item.href} {...externalProps}>
          {item.text}
        </a>
        {item.suffix || null}
      </p>
    );
  }

  return (
    <p key={index}>
      {item.strong ? <strong>{item.text}</strong> : item.text}
    </p>
  );
};

export const AnnouncementBody = ({ blocks = [] }) => <>{blocks.map(renderBodyItem)}</>;

const formatStep = (template, current, total) =>
  template.replace("{current}", current).replace("{total}", total);

const AnnouncementModal = ({
  announcement,
  onDismiss,
  onNext,
  stepIndex = 0,
  stepCount = 1,
  dismissible = true,
  acknowledgeLabel,
  closeLabel,
  doNotShowAgain = null,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const titleId = useId();
  const bodyId = useId();
  const checkboxId = useId();
  const isLastStep = stepIndex >= stepCount - 1;
  const showStepIndicator = stepCount > 1;

  useDialogScrollLock();
  useDialogFocusTrap(dialogRef, {
    onEscape: dismissible ? onDismiss : undefined,
    focusKey: stepIndex,
  });

  return createPortal(
    <div
      className="announcement-modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
    >
      <section
        ref={dialogRef}
        className="announcement-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex="-1"
      >
        <header className="announcement-modal__header">
          <div className="announcement-modal__heading">
            {showStepIndicator && (
              <p className="announcement-modal__step">
                {formatStep(t("notifications.stepOf"), stepIndex + 1, stepCount)}
              </p>
            )}
            <h2 id={titleId}>{announcement.title}</h2>
          </div>
          {dismissible && (
            <button
              type="button"
              className="announcement-modal__close"
              aria-label={closeLabel || t("notifications.closeAnnouncement")}
              onClick={onDismiss}
            >
              ×
            </button>
          )}
        </header>
        <div id={bodyId} className="announcement-modal__body">
          <AnnouncementBody blocks={announcement.body} />
        </div>
        <footer
          className={`announcement-modal__footer${
            doNotShowAgain ? " announcement-modal__footer--with-option" : ""
          }`}
        >
          {doNotShowAgain && (
            <label className="announcement-modal__option" htmlFor={checkboxId}>
              <input
                id={checkboxId}
                type="checkbox"
                checked={doNotShowAgain.checked}
                onChange={(event) => doNotShowAgain.onChange(event.target.checked)}
              />
              <span>{t("notifications.doNotShowAgain")}</span>
            </label>
          )}
          {isLastStep ? (
            <button
              type="button"
              className="announcement-modal__acknowledge"
              onClick={onDismiss}
            >
              {acknowledgeLabel || t("notifications.acknowledge")}
            </button>
          ) : (
            <button
              type="button"
              className="announcement-modal__acknowledge"
              onClick={onNext}
            >
              {t("notifications.next")}
            </button>
          )}
        </footer>
      </section>
    </div>,
    document.body,
  );
};

export default AnnouncementModal;
